'use client';

import { useState, useEffect } from 'react';
import { userServiceRequestService, ServiceRequest } from '@/lib/services/userServiceRequestService';

interface ServiceRequestsProps {
  profileId: string;
}

export default function ServiceRequests({ profileId }: ServiceRequestsProps) {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('active');

  useEffect(() => {
    loadRequests();
  }, [profileId, filter]);

  const loadRequests = async () => {
    try {
      if (filter === 'active') {
        const data = await userServiceRequestService.getActiveRequests(profileId);
        setRequests(data);
      } else {
        const data = await userServiceRequestService.getRequestsByProfileId(profileId);
        if (filter === 'all') {
          setRequests(data);
        } else {
          setRequests(data.filter(req => req.status === filter));
        }
      }
    } catch (error: any) {
      console.error('Failed to load requests:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      confirmed: { bg: 'bg-blue-100', text: 'text-blue-800' },
      in_progress: { bg: 'bg-purple-100', text: 'text-purple-800' },
      completed: { bg: 'bg-green-100', text: 'text-green-800' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig: Record<string, { bg: string; text: string }> = {
      low: { bg: 'bg-gray-100', text: 'text-gray-800' },
      normal: { bg: 'bg-blue-100', text: 'text-blue-800' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800' },
      urgent: { bg: 'bg-red-100', text: 'text-red-800' },
    };

    const config = priorityConfig[priority] || priorityConfig.normal;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex space-x-4">
          {['active', 'all', 'completed', 'cancelled'].map((filterOption) => (
            <button
              key={filterOption}
              onClick={() => setFilter(filterOption as any)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === filterOption
                  ? 'bg-brand-purple text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <h2 className="text-xl font-bold text-dark-navy mb-4">Service Requests</h2>
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-body-text">No service requests found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:border-brand-purple transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-dark-navy">{request.serviceType.replace('_', ' ')}</h3>
                      {getStatusBadge(request.status || 'pending')}
                      {getPriorityBadge(request.priority || 'normal')}
                    </div>
                    <p className="text-sm text-body-text mb-2">{request.itemDescription}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-body-text">Pickup</p>
                    <p className="text-dark-navy font-medium">{request.pickupAddress}</p>
                  </div>
                  <div>
                    <p className="text-body-text">Delivery</p>
                    <p className="text-dark-navy font-medium">{request.deliveryAddress}</p>
                  </div>
                  <div>
                    <p className="text-body-text">Pickup Date</p>
                    <p className="text-dark-navy font-medium">
                      {new Date(request.pickupDate).toLocaleDateString()}
                      {request.pickupTime && ` at ${request.pickupTime}`}
                    </p>
                  </div>
                  {request.estimatedPrice && (
                    <div>
                      <p className="text-body-text">Estimated Price</p>
                      <p className="text-dark-navy font-medium">${request.estimatedPrice.toFixed(2)}</p>
                    </div>
                  )}
                </div>

                {request.specialInstructions && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-body-text mb-1">Special Instructions</p>
                    <p className="text-sm text-dark-navy">{request.specialInstructions}</p>
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between text-xs text-body-text">
                  <span>Created: {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'N/A'}</span>
                  {request.assignedDriverId && (
                    <span className="text-brand-purple font-medium">Driver Assigned</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}